<template>
  <div :id="modalId" class="modal overflow-y-auto flex" tabindex="-1" aria-hidden="true" :class="{ show: isOpen }">
    <div class="modal-dialog">
      <div class="modal-content">
        <!-- Modal Header -->
        <div class="modal-header px-5 py-3 border-b border-gray-200 dark:border-dark-5">
          <h2 class="font-medium text-base">
            {{ $t(`${viewRoot}.new_supplement`) }}
          </h2>
        </div>

        <!-- Modal Body -->
        <div class="modal-body px-5 py-6">
          <!-- Dynamic Tabs -->
          <div class="lang__tabs nav nav-tabs intro-y flex-col sm:flex-row text-gray-600 rounded-b-none" role="tablist">
            <a
              v-for="lang in langs"
              :key="lang.key"
              @click="changeLang(lang.key)"
              href="javascript:;"
              class="tooltip w-full sm:w-40 py-4 text-center flex justify-center items-center rounded-t-md"
              :class="activeLangKey === lang.key ? 'active border-transparent' : ''"
            >
              {{ $t("components.langs." + lang.key + ".name") }}
            </a>
          </div>

          <!-- Language Inputs -->
          <div class="mt-5">
            <div v-for="lang in langs" :key="lang.key" v-show="activeLangKey === lang.key">
              <!-- Nombre comercial (requerido) -->
              <label :for="'quick-supplement-name-' + lang.key" class="form-label font-semibold">
                {{ $t(`${viewRoot}.text51`) }}
              </label>
              <div class="input-group">
                <input
                  :id="'quick-supplement-name-' + lang.key"
                  type="text"
                  class="form-control w-full bg-theme-32"
                  :class="validationError && !supplement.langs[lang.key].name.trim() ? 'border border-theme-6' : ''"
                  :placeholder="$t(`${viewRoot}.text51`)"
                  v-model="supplement.langs[lang.key].name"
                  @input="clearValidationError"
                  maxlength="255"
                />
              </div>

              <!-- Nombre químico (opcional) -->
              <div class="mt-4">
                <label :for="'quick-supplement-chemical-' + lang.key" class="form-label font-semibold">
                  {{ $t(`${viewRoot}.text49`) }}
                </label>
                <div class="input-group">
                  <input
                    :id="'quick-supplement-chemical-' + lang.key"
                    type="text"
                    class="form-control w-full bg-theme-32"
                    :placeholder="$t(`${viewRoot}.text49`)"
                    v-model="supplement.langs[lang.key].chemicalName"
                    maxlength="255"
                  />
                </div>
              </div>

              <!-- Tipo / Variedad (opcional) -->
              <div class="mt-4">
                <label :for="'quick-supplement-family-' + lang.key" class="form-label font-semibold">
                  {{ $t(`${viewRoot}.text50`) }}
                </label>
                <div class="input-group">
                  <input
                    :id="'quick-supplement-family-' + lang.key"
                    type="text"
                    class="form-control w-full bg-theme-32"
                    :placeholder="$t(`${viewRoot}.text50`)"
                    v-model="supplement.langs[lang.key].familySupplement"
                    maxlength="255"
                  />
                </div>
              </div>

              <!-- Categoría (opcional) -->
              <div class="mt-5">
                <label for="quick-supplement-categories" class="form-label font-semibold">
                  {{ $t(`${viewRoot}.text12`) }}
                </label>
                <div class="input-group input-bg-theme-32 rounded relative">
                  <TomSelect
                    id="quick-supplement-categories"
                    v-model="selectedCategories"
                    class="w-full cursor"
                    :placeholder="$t(`${viewRoot}.text13`)"
                    :plugins="['dropdown_input']"
                    :multiple="true"
                    :max="5"
                  >
                    <option v-for="c in categories" :key="c.id" :value="c.id">
                      {{ c.name }}
                    </option>
                  </TomSelect>
                </div>
              </div>

              <!-- Descripción (opcional) -->
              <div class="mt-4">
                <label :for="'quick-supplement-desc-' + lang.key" class="form-label font-semibold">
                  {{ $t(`${viewRoot}.text7`) }}
                </label>
                <div class="input-group">
                  <textarea
                    :id="'quick-supplement-desc-' + lang.key"
                    rows="3"
                    class="form-control w-full bg-theme-32"
                    :placeholder="$t(`${viewRoot}.text14`)"
                    v-model="supplement.langs[lang.key].description"
                    maxlength="255"
                  ></textarea>
                </div>
              </div>

              <!-- Sirve para (opcional) -->
              <div class="mt-5">
                <label for="quick-supplement-purposes" class="form-label font-semibold">
                  {{ $t(`${viewRoot}.text54`) }}
                </label>
                <div class="input-group input-bg-theme-32 rounded relative">
                  <TomSelect
                    id="quick-supplement-purposes"
                    v-model="selectedPurposes"
                    class="w-full cursor"
                    :placeholder="$t(`${viewRoot}.text30`)"
                    :plugins="['dropdown_input']"
                    :multiple="true"
                  >
                    <option v-for="p in purposes" :key="p.id" :value="String(p.id)">
                      {{ p.name }}
                    </option>
                  </TomSelect>
                </div>
              </div>

              <!-- Comentarios generales (opcional) -->
              <div class="mt-4">
                <label :for="'quick-supplement-comments-' + lang.key" class="form-label font-semibold">
                  {{ $t(`${viewRoot}.text73`) }}
                </label>
                <div class="input-group">
                  <textarea
                    :id="'quick-supplement-comments-' + lang.key"
                    rows="3"
                    class="form-control w-full bg-theme-32"
                    :placeholder="$t(`${viewRoot}.text73`)"
                    v-model="supplement.langs[lang.key].comments"
                    maxlength="500"
                  ></textarea>
                </div>
              </div>
            </div>
          </div>

          <!-- Error messages -->
          <div v-if="validationError" class="text-theme-6 mt-3 text-sm font-semibold">
            {{ validationError }}
          </div>
          <div v-if="apiError" class="text-theme-6 mt-3 text-sm font-semibold">
            {{ apiError }}
          </div>
        </div>

        <!-- Modal Footer -->
        <div
          class="modal-footer px-5 py-3 border-t border-gray-200 dark:border-dark-5 text-right flex justify-end gap-3"
        >
          <button type="button" @click="close" class="btn btn-outline-secondary w-24 mr-2" :disabled="loading">
            {{ $t("components.cancel") }}
          </button>
          <button
            type="button"
            @click="save"
            class="btn btn-primary w-24 flex items-center justify-center"
            :disabled="loading"
          >
            <LoadingIcon v-if="loading" icon="three-dots" class="w-4 h-4 ml-2" />
            <span v-else>{{ $t("views.save") }}</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent } from "vue";
import NutrihubService from "@/services/nutrihub.service";
import SupplementService from "@/services/supplements.service";
import Toastify from "toastify-js";
import { formatToSend } from "@/utils/modules/nutrihub/supplements";

export default defineComponent({
  name: "SupplementQuickCreateModal",
  props: {
    modalId: {
      type: String,
      default: "supplement-quick-create-modal",
    },
  },
  emits: ["created"],
  data() {
    return {
      viewRoot: "views.supplements",
      isOpen: false,
      loading: false,
      langs: this.$global.getLangs(),
      activeLangKey: "es",
      supplement: { langs: {} },
      categories: [],
      selectedCategories: [],
      purposes: [],
      selectedPurposes: [],
      validationError: "",
      apiError: "",
    };
  },
  created() {
    this.resetForm();
  },
  methods: {
    buildLangsShape() {
      const o = {};
      (this.langs || []).forEach(({ key }) => {
        o[key] = { name: "", chemicalName: "", familySupplement: "", description: "", comments: "" };
      });
      return o;
    },
    resetForm() {
      this.activeLangKey = this.langs[0]?.key || "es";
      this.supplement = { langs: this.buildLangsShape() };
      this.categories = [];
      this.selectedCategories = [];
      this.purposes = [];
      this.selectedPurposes = [];
      this.validationError = "";
      this.apiError = "";
    },
    async show() {
      this.resetForm();
      this.isOpen = true;
      cash("#" + this.modalId).modal("show");
      await Promise.all([this.loadCategories(), this.loadPurposes()]);
    },
    close() {
      this.isOpen = false;
      cash("#" + this.modalId).modal("hide");
    },
    changeLang(key) {
      this.activeLangKey = key;
    },
    clearValidationError() {
      this.validationError = "";
    },
    async loadCategories() {
      try {
        const res = await SupplementService.getCategories(localStorage.lang);
        this.categories = Array.isArray(res) ? res : res?.data || [];
      } catch {
        this.categories = [];
      }
    },
    async loadPurposes() {
      try {
        const response = await NutrihubService.getPurposes(localStorage.lang);
        const list = Array.isArray(response) ? response : response?.data || [];
        this.purposes = list.map((p) => ({
          id: p.id,
          name: p.name || (p.langs?.find((l) => l.lang === localStorage.lang)?.value ?? ""),
        }));
      } catch {
        this.purposes = [];
      }
    },
    validate() {
      const missing = [];
      (this.langs || []).forEach(({ key }) => {
        if (!String(this.supplement.langs[key]?.name || "").trim()) missing.push(key);
      });

      if (missing.length > 0) {
        const names = missing.map((k) => this.$t(`components.langs.${k}.name`)).join(", ");
        this.validationError = `${this.$t("views.supplements.text95")} (${names})`;
        return false;
      }
      return true;
    },
    async save() {
      this.validationError = "";
      this.apiError = "";

      if (!this.validate()) return;

      this.loading = true;

      try {
        const langs = {};
        Object.entries(this.supplement.langs).forEach(([key, l]) => {
          langs[key] = {
            name: l.name,
            chemicalName: l.chemicalName,
            familySupplement: l.familySupplement,
            description: l.description,
            comments: l.comments,
          };
        });

        const mini = {
          id: null,
          langs,
          usedFor: this.selectedPurposes,
          benefits: [],
          doses: [],
          scienceSources: { east: [], west: [] },
          status: "PENDING_APPROVAL",
        };

        const [payload] = formatToSend(mini, this.selectedCategories);
        const data = await NutrihubService.createSupplement(payload);

        const createdId = data?.id || data?.data?.id || data?.data?.data?.id;
        if (!createdId) {
          this.apiError = this.$t(`${this.viewRoot}.save_error`) || "Error al crear el suplemento.";
          return;
        }

        const name =
          this.supplement.langs[this.activeLangKey]?.name || Object.values(this.supplement.langs)[0]?.name || "";

        this.$emit("created", {
          supplement: { id: createdId, name },
        });

        this.showToast();
        this.close();
      } catch (error) {
        console.error("Error creating supplement via quick modal:", error);
        this.apiError = error?.response?.data?.message || error?.message || "Error al crear el suplemento.";
      } finally {
        this.loading = false;
      }
    },
    showToast() {
      const id = "saved-confirmation-success";
      const node = cash(`#${id}`).clone().removeClass("hidden")[0];
      if (node) {
        Toastify({
          node: node,
          duration: 3000,
          gravity: "top",
          position: "right",
          close: true,
        }).showToast();
      } else {
        const customNode = cash(`
          <div class="toastify-content flex">
            <div class="text-theme-9">✓</div>
            <div class="ml-4 mr-4">
              <div class="font-medium">${this.$t("components.message_modal.saved") || "Guardado con éxito."}</div>
            </div>
          </div>
        `)[0];
        Toastify({
          node: customNode,
          duration: 3000,
          gravity: "top",
          position: "right",
          close: true,
        }).showToast();
      }
    },
  },
});
</script>
